import React, { useMemo } from 'react';
import { Save, Trash2 } from 'lucide-react';
import EmergencyTeamTable from './EmergencyTeamTable';
import { buildMonthCalendar, monthKey, monthLabel, weekKeyForDate } from './helpers';
import {
  boardStyle,
  calendarStyle,
  cellStyle,
  currentWeekStyle,
  dateRowStyle,
  dayStyle,
  emptyCellStyle,
  gridStyle,
  holidayCellStyle,
  holidayStyle,
  mobileAssignmentListStyle,
  mobileIconButtonStyle,
  mobileWeekActionsStyle,
  mobileWeekCardStyle,
  mobileWeekDateStyle,
  mobileWeekSelectStyle,
  monthCardStyle,
  monthTitleStyle,
  nameStyle,
  noteButtonStyle,
  noteHeaderStyle,
  noteStyle,
  saturdayCellStyle,
  saturdayWeekdayStyle,
  sundayCellStyle,
  sundayWeekdayStyle,
  textareaStyle,
  todayStyle,
  weekdayGrid,
  weekdayStyle,
  wrapStyle,
} from './EmergencyTeamCalendar.styles';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export default function EmergencyTeamCalendar({
  months,
  users,
  drafts,
  monthNotes,
  onDraft,
  onMonthNote,
  onSave,
  onSaveMonthNote,
  onDelete,
  savingKey,
  savingNoteKey,
  today,
  sliding,
  slideDirection,
  isMobile = false,
}) {
  const calendarMonths = isMobile ? (months || []).slice(0, 1) : (months || []);

  return (
    <div style={wrapStyle}>
      <div style={boardStyle(sliding, slideDirection, isMobile)}>
        {calendarMonths.map((item) => {
          const key = monthKey(item.year, item.month);
          return (
            <section key={key} style={monthCardStyle}>
              <MonthCalendar
                year={item.year}
                month={item.month}
                weeks={item.weeks || []}
                holidays={item.holidays || []}
                today={today}
              />
              {isMobile ? (
                <MobileAssignmentList
                  weeks={item.weeks || []}
                  users={users || []}
                  drafts={drafts}
                  onDraft={onDraft}
                  onSave={onSave}
                  onDelete={onDelete}
                  savingKey={savingKey}
                />
              ) : (
                <EmergencyTeamTable
                  weeks={item.weeks || []}
                  users={users || []}
                  drafts={drafts}
                  onDraft={onDraft}
                  onSave={onSave}
                  onDelete={onDelete}
                  savingKey={savingKey}
                />
              )}
              <MonthNoteEditor
                value={monthNotes?.[key] || ''}
                onChange={(value) => onMonthNote(item.year, item.month, value)}
                onSave={() => onSaveMonthNote(item.year, item.month)}
                saving={savingNoteKey === key}
              />
            </section>
          );
        })}
      </div>
    </div>
  );
}

function MonthCalendar({ year, month, weeks, holidays, today }) {
  const cells = useMemo(() => buildMonthCalendar(year, month), [year, month]);
  const weekMap = useMemo(() => {
    return new Map((weeks || []).map((week) => [week.week_start, week]));
  }, [weeks]);
  const holidayMap = useMemo(() => {
    return new Map((holidays || []).map((holiday) => [holiday.ymd, holiday]));
  }, [holidays]);

  const currentWeek = useMemo(() => {
    const viewingMonth = `${year}-${String(month).padStart(2, '0')}`;
    if (today.slice(0, 7) !== viewingMonth) return null;
    return (weeks || []).find((week) => today <= week.week_end) || null;
  }, [month, today, weeks, year]);

  return (
    <div style={calendarStyle}>
      <div style={monthTitleStyle}>{monthLabel(year, month)}</div>
      <div style={weekdayGrid}>
        {WEEKDAYS.map((day, index) => (
          <div
            key={day}
            style={{
              ...weekdayStyle,
              ...(index === 0 ? sundayWeekdayStyle : {}),
              ...(index === 6 ? saturdayWeekdayStyle : {}),
            }}
          >
            {day}
          </div>
        ))}
      </div>
      <div style={gridStyle}>
        {cells.map((cell, index) => {
          if (!cell) return <div key={`blank-${index}`} style={emptyCellStyle} />;
          const weekKey = weekKeyForDate(cell.date);
          const week = weekKey ? weekMap.get(weekKey) : null;
          const assignment = week?.assignment;
          const userName = assignment?.user_name || assignment?.assigned_name || '';
          const isSunday = cell.dow === 0;
          const isSaturday = cell.dow === 6;
          const isWeekend = isSunday || isSaturday;
          const isToday = cell.date === today;
          const isCurrentWeek = currentWeek?.week_start && week?.week_start === currentWeek.week_start;
          const holiday = holidayMap.get(cell.date);
          const isHoliday = !!holiday;

          return (
            <div
              key={cell.date}
              style={{
                ...cellStyle,
                ...(isSunday ? sundayCellStyle : {}),
                ...(isSaturday ? saturdayCellStyle : {}),
                ...(isHoliday ? holidayCellStyle : {}),
                ...(isCurrentWeek ? currentWeekStyle : {}),
                ...(isToday ? todayStyle : {}),
              }}
            >
              <div style={dateRowStyle}>
                <span style={dayStyle(isSunday, isSaturday, isHoliday)}>{cell.day}</span>
                {isHoliday && (
                  <span style={holidayStyle} title={holiday.name || '공휴일'}>
                    {holiday.name || '공휴일'}
                  </span>
                )}
              </div>
              {isWeekend && (
                <div style={nameStyle(userName)} title={userName || '미배정'}>
                  {userName || '미배정'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobileAssignmentList({
  weeks,
  users,
  drafts,
  onDraft,
  onSave,
  onDelete,
  savingKey,
}) {
  return (
    <div style={mobileAssignmentListStyle}>
      {(weeks || []).map((week) => {
        const draft = drafts[week.week_start] || {};
        const isSaving = savingKey === week.week_start;
        return (
          <div key={week.week_start} style={mobileWeekCardStyle}>
            <div style={mobileWeekDateStyle}>
              <strong>{shortDate(week.week_start)} ~ {shortDate(week.week_end)}</strong>
              <span>{draft.user_id ? '배정됨' : '미배정'}</span>
            </div>
            <select
              value={draft.user_id || ''}
              onChange={(event) => onDraft(week.week_start, { user_id: event.target.value })}
              style={mobileWeekSelectStyle}
            >
              <option value="">선택</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
            <div style={mobileWeekActionsStyle}>
              <button
                type="button"
                onClick={() => onSave(week.week_start)}
                disabled={isSaving || !draft.user_id}
                style={mobileIconButtonStyle('#2563eb', isSaving || !draft.user_id)}
                title="저장"
              >
                <Save size={14} />
                저장
              </button>
              {draft.id && (
                <button
                  type="button"
                  onClick={() => onDelete(week.week_start)}
                  disabled={isSaving}
                  style={mobileIconButtonStyle('#dc2626', isSaving)}
                  title="삭제"
                >
                  <Trash2 size={14} />
                  삭제
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthNoteEditor({ value, onChange, onSave, saving }) {
  return (
    <div style={noteStyle}>
      <div style={noteHeaderStyle}>
        <span>메모</span>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          style={noteButtonStyle(saving)}
          title="메모 저장"
        >
          <Save size={13} />
          저장
        </button>
      </div>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={textareaStyle}
        rows={2}
        placeholder="메모"
      />
    </div>
  );
}

function shortDate(value) {
  return String(value || '').slice(5);
}
